import { ApmBase } from "@elastic/apm-rum";
import { mm, Viewer } from "@inovitas/infra3dsdk";
import { Layer } from "@inovitas/infra3dsdk/dist/types/mm";
import { AlertColor } from "@mui/material";
import { create } from "zustand";

import { campaignType } from "shared/ui/topbar/items/campaign/Campaign";
import { ProviderType } from "shared/common/provider/pilegateprovider";
import { LinRefConfig } from "shared/common/utils/linref/addLinRefLayers";
import { Manager } from "../services/manager";

type AlertMessage = {
  message: string | Error | null;
  type: AlertColor;
};

type projectScope = {
  boundaries: any; // GeoJSON Polygon Feature
  campaign_uids: string[] | undefined;
  start_timestamp: string | undefined;
  end_timestamp: string | undefined;
  sequence_uids: string[] | undefined;
};

type projectConfig = {
  collection: string;
  created_at: string;
  created_by: string;
  description: string;
  name: string;
  scope: projectScope | undefined;
  start_up_frame: string;
  tags: any;
  uid: string;
};

type geocoderConfig = {
  name: string;
  datasets: string[];
};

export type basemapConfig = {
  type: "WMTS" | "WMS" | "OSM";
  name: string;
  visible?: boolean;
  zIndex?: number;
  opacity?: number;
  capabilities?: string;
  options?: any;
  icon?: string;
};

export type clientConfig = {
  map: {
    geo_coders?: geocoderConfig[];
    base_layers?: basemapConfig[];
    overlay_layers?: basemapConfig[];
    show_segmented_only?: boolean;
  };
  uid: string;
  epsg_height: number;
  epsg: number;
  render_distance: number;
  anon_project: string;
  framer_version: "2" | "3";
  linref?: LinRefConfig;
};
export interface defaultStoreInterface {
  apm: ApmBase | undefined;
  changeApm: (state: ApmBase | undefined) => void;
  darkTheme: boolean;
  changeDarkTheme: (state: boolean) => void;
  viewer: Viewer | undefined;
  changeViewer: (viewer: Viewer | undefined) => void;
  mapExpand: boolean;
  changeMapExpand: (state: boolean) => void;
  showCockpit: boolean;
  changeShowCockpit: (state: boolean) => void;
  showMapWindow: boolean;
  changeShowMapWindow: (state: boolean) => void;
  showToolbar: boolean;
  changeShowToolbar: (state: boolean) => void;
  showTopbar: boolean;
  changeShowTopbar: (state: boolean) => void;
  showStreetMap: boolean;
  changeShowStreetMap: (state: boolean) => void;
  showAxisscale: boolean | undefined;
  changeShowAxisscale: (state: boolean | undefined) => void;
  loading: boolean;
  changeLoading: (state: boolean) => void;
  activeCampaign: campaignType | undefined;
  changeActiveCampaign: (state: campaignType | undefined) => void;
  reportAnonymization: boolean;
  changeReportAnonymization: (state: boolean) => void;
  introductionStep: number;
  changeIntroductionStep: (state: number) => void;
  riaLayer: Layer | undefined;
  changeRiaLayer: (state: Layer | undefined) => void;
  layerConfiguration: any;
  changeLayerConfiguration: (state: any) => void;
  layersToAdd: any;
  changeLayersToAdd: (state: any) => void;
  // resetLayerConfiguration: boolean;
  // changeResetLayerConfiguration: (state: boolean) => void;
  showIntroduction: boolean;
  changeShowIntroduction: (state: boolean) => void;
  projectConfig: projectConfig | undefined;
  changeProjectConfig: (state: projectConfig | undefined) => void;
  clientConfig: clientConfig | undefined;
  changeClientConfig: (state: clientConfig) => void;
  alertSaveLayer: boolean;
  changeAlertSaveLayer: (state: boolean) => void;
  alertSaveOrDiscardLastLayer: boolean;
  changeAlertSaveOrDiscardLastLayer: (state: boolean) => void;
  alertDeleteFeature: boolean;
  changeAlertDeleteFeature: (state: boolean) => void;
  deleteFeatureWithID: string | undefined;
  changeDeleteFeatureWithID: (state: string | undefined) => void;
  manager: Manager | undefined;
  changeManager: (state: Manager) => void;
  editLayerSettings: mm.Layer | undefined;
  changeEditLayerSettings: (state: mm.Layer | undefined) => void;
  alertMessage: AlertMessage | undefined;
  changeAlertMessage: (state: AlertMessage | undefined) => void;
  cockpitPosition: boolean;
  changeCockpitPosition: (state: boolean) => void;
  providerType: ProviderType | undefined;
  changeProviderType: (state: ProviderType | undefined) => void;
  showPCLExport: boolean;
  changeShowPCLExport: (state: boolean) => void;
  showWhatsNew: boolean;
  changeShowWhatsNew: (state: boolean) => void;
  cameraSliderId: number;
  changeCameraSliderId: (state: number) => void;
  axisCollection: mm.AxisObject[];
  changeAxisCollection: (state: mm.AxisObject[]) => void;
  networkLayer: mm.NetworkLayer | undefined;
  changeNetworkLayer: (state: mm.NetworkLayer | undefined) => void;
  authMode: "view" | "edit";
  changeAuthMode: (state: "view" | "edit") => void;
}

export const defaultStore = create<defaultStoreInterface>()((set) => ({
  apm: undefined,
  changeApm: (state: ApmBase | undefined) => set(() => ({ apm: state })),

  darkTheme: localStorage.getItem("theme") === "dark",
  changeDarkTheme: (state: boolean) => set(() => ({ darkTheme: state })),

  loading: true,
  changeLoading: (state: boolean) => set(() => ({ loading: state })),

  viewer: undefined,
  changeViewer: (viewer: Viewer | undefined) => set(() => ({ viewer: viewer })),

  mapExpand: true,
  changeMapExpand: (state: boolean) => set(() => ({ mapExpand: state })),

  showCockpit: true,
  changeShowCockpit: (state: boolean) => set(() => ({ showCockpit: state })),

  showMapWindow: true,
  changeShowMapWindow: (state: boolean) =>
    set(() => ({ showMapWindow: state })),

  showToolbar: true,
  changeShowToolbar: (state: boolean) => set(() => ({ showToolbar: state })),

  showTopbar: true,
  changeShowTopbar: (state: boolean) => set(() => ({ showTopbar: state })),

  showStreetMap: false,
  changeShowStreetMap: (state: boolean) =>
    set(() => ({ showStreetMap: state })),

  showAxisscale: undefined,
  changeShowAxisscale: (state: boolean | undefined) =>
    set(() => ({ showAxisscale: state })),

  activeCampaign: undefined,
  changeActiveCampaign: (campaign: campaignType | undefined) =>
    set(() => ({ activeCampaign: campaign })),

  reportAnonymization: false,
  changeReportAnonymization: (state: boolean) =>
    set(() => ({ reportAnonymization: state })),

  introductionStep: 0,
  changeIntroductionStep: (state: number) =>
    set(() => ({ introductionStep: state })),

  riaLayer: undefined,
  changeRiaLayer: (state: Layer | undefined) =>
    set(() => ({ riaLayer: state })),

  layerConfiguration: undefined,
  changeLayerConfiguration: (state: any) =>
    set(() => ({ layerConfiguration: state })),

  layersToAdd: undefined,
  changeLayersToAdd: (state: any) => set(() => ({ layersToAdd: state })),

  // resetLayerConfiguration: false,
  // changeResetLayerConfiguration: (state: boolean) =>
  //   set(() => ({ resetLayerConfiguration: state })),

  showIntroduction: false,
  changeShowIntroduction: (state: boolean) =>
    set(() => ({ showIntroduction: state })),

  projectConfig: undefined,
  changeProjectConfig: (state: projectConfig | undefined) =>
    set(() => ({ projectConfig: state })),

  clientConfig: undefined,
  changeClientConfig: (state: clientConfig) =>
    set(() => ({ clientConfig: state })),

  alertSaveLayer: false,
  changeAlertSaveLayer: (state: boolean) =>
    set(() => ({ alertSaveLayer: state })),

  alertSaveOrDiscardLastLayer: false,
  changeAlertSaveOrDiscardLastLayer: (state: boolean) =>
    set(() => ({ alertSaveOrDiscardLastLayer: state })),

  alertDeleteFeature: false,
  changeAlertDeleteFeature: (state: boolean) =>
    set(() => ({ alertDeleteFeature: state })),

  deleteFeatureWithID: undefined,
  changeDeleteFeatureWithID: (state) =>
    set(() => ({ deleteFeatureWithID: state })),

  manager: undefined,
  changeManager: (state: Manager) => set(() => ({ manager: state })),

  editLayerSettings: undefined,
  changeEditLayerSettings: (state: mm.Layer | undefined) =>
    set(() => ({ editLayerSettings: state })),

  alertMessage: undefined,
  changeAlertMessage: (state: AlertMessage | undefined) =>
    set(() => ({ alertMessage: state })),

  cockpitPosition: false,
  changeCockpitPosition: (state: boolean) =>
    set(() => ({ cockpitPosition: state })),

  providerType: undefined,
  changeProviderType: (state: ProviderType | undefined) =>
    set(() => ({ providerType: state })),

  showPCLExport: false,
  changeShowPCLExport: (state: boolean) =>
    set(() => ({ showPCLExport: state })),

  showWhatsNew: false,
  changeShowWhatsNew: (state: boolean) => set(() => ({ showWhatsNew: state })),

  cameraSliderId: 0,
  changeCameraSliderId: (state: number) =>
    set(() => ({ cameraSliderId: state })),

  axisCollection: [],
  changeAxisCollection: (state: mm.AxisObject[]) =>
    set(() => ({ axisCollection: state })),

  networkLayer: undefined,
  changeNetworkLayer: (state: mm.NetworkLayer | undefined) =>
    set(() => ({ networkLayer: state })),

  authMode: "view",
  changeAuthMode: (state: "view" | "edit") => set(() => ({ authMode: state })),
}));
